import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../api/axios";
import endpoints from "../../api/endpoints";
import {
  downloadCsvFile,
  resolveCsvFileName,
} from "../../utility/CsvReportUtility";


interface DownloadTestSeriesReportArgs {
  pageId: number | string | Array<number | string>;
  nuid: string;
  sortColumn?: string;
  sortOrder?: "ASC" | "DESC";
  fileName?: string;
}

interface DownloadMembersRolesReportArgs {
  nuid: string;
  pageId?: string;
  sortColumn?: string;
  sortOrder?: "ASC" | "DESC";
}

interface DownloadPipHistoryReportArgs {
  nuid: string;
  pageId: "myTeams" | "pipId" | "searchProject";
  teamId?: string;
  sortColumn?: string;
  sortOrder?: "ASC" | "DESC";
  // Search filter params for searchProject mode
  region?: string;
  area?: string;
  center?: string;
  location?: string;
  entity?: string;
  costCenter?: string;
  focusArea?: string;
  metric?: string;
  valueCompass?: string;
  result?: string;
  rating?: string;
  searchBy?: string;
  searchString?: string;
  searchByProject?: string;
  perfStatus?: string;
  /** UBT ID range filters used by Search Projects. */
  fromId?: string;
  toId?: string;
}

/**
 * Arguments for exporting GL data filtered by the teams belonging to the
 * authenticated user.  The back-end resolves team IDs from the NUID header.
 */
interface ExportGLDataByTeamIdsArgs {
  nuid: string;
}

/**
 * Arguments for downloading a test-series CSV report filtered by arbitrary
 * search criteria (used by the SearchUBTProjects page).
 * All filter params are pre-built by the caller from session storage.
 */
interface DownloadTestSeriesByCriteriaArgs {
  /** Authenticated user's NUID (sent as `x-nuid` request header). */
  nuid: string;
  /**
   * Pre-built query-parameter map sourced from session storage.
   * Keys must match the `/ubt/reports/test-series` API contract.
   */
  params: Record<string, string>;
}

interface TestSeriesReportState {
  rows: Array<Record<string, unknown>>;
  loading: boolean;
  error: string | null;
  lastDownloadedFileName: string | null;
}

const TEST_SERIES_DEFAULT_FILENAME = "TEST-SERIES-REPORT.csv";
const MEMBERS_ROLES_DEFAULT_FILENAME = "UBT_MEMBERS_AND_ROLES.csv";
const PIP_HISTORY_DEFAULT_FILENAME = "PIP_HISTORY.csv";

const initialState: TestSeriesReportState = {
  rows: [],
  loading: false,
  error: null,
  lastDownloadedFileName: null,
};



const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  return fallback;
};

/**
 * Builds query params for PIP History report API call.
 * Conditionally includes only non-empty values.
 */
const buildPipHistoryQueryParams = (args: DownloadPipHistoryReportArgs): Record<string, string> => {
  const fieldMappings: Array<{ key: keyof DownloadPipHistoryReportArgs; paramName: string }> = [
    { key: 'pageId', paramName: 'pageId' },
    { key: 'teamId', paramName: 'teamId' },
    { key: 'sortColumn', paramName: 'sortColumn' },
    { key: 'sortOrder', paramName: 'sortOrder' },
    { key: 'region', paramName: 'region' },
    { key: 'area', paramName: 'area' },
    { key: 'center', paramName: 'center' },
    { key: 'location', paramName: 'location' },
    { key: 'entity', paramName: 'entity' },
    { key: 'costCenter', paramName: 'costCenter' },
    { key: 'focusArea', paramName: 'focusArea' },
    { key: 'metric', paramName: 'metric' },
    { key: 'valueCompass', paramName: 'valueCompass' },
    { key: 'result', paramName: 'result' },
    { key: 'rating', paramName: 'rating' },
    { key: 'searchBy', paramName: 'searchBy' },
    { key: 'searchString', paramName: 'searchString' },
    { key: 'searchByProject', paramName: 'searchByProject' },
    { key: 'perfStatus', paramName: 'perfStatus' },
    { key: 'fromId', paramName: 'fromId' },
    { key: 'toId', paramName: 'toId' },
  ];

  return fieldMappings.reduce((params, { key, paramName }) => {
    const value = args[key];
    if (value) {
      params[paramName] = String(value);
    }
    return params;
  }, {} as Record<string, string>);
};

/**
 * Fetches the test-series CSV report and starts a file download.
 * @param args - Team filtering and requester metadata.
 * @returns Download metadata for the completed request.
 */
export const downloadTestSeriesReport = createAsyncThunk(
  "teamReport/downloadTestSeriesReport",
  async (
    {
      pageId,
      nuid,
      sortColumn,
      sortOrder,
      fileName,
    }: DownloadTestSeriesReportArgs,
    { rejectWithValue },
  ) => {
    try {
      const response = await api.get(endpoints.ubt.reports.testSeries, {
        params: {
          pageId,
          ...(sortColumn ? { sortColumn: "teamId" } : {}),
          ...(sortOrder ? { sortOrder } : {}),
        },
        headers: {
          accept: "text/csv",
          "x-nuid": nuid,
        },
        responseType: "blob",
      });

      const resolvedFileName = resolveCsvFileName(
        response.headers?.["content-disposition"],
        fileName?.trim() ?? TEST_SERIES_DEFAULT_FILENAME,
      );

      downloadCsvFile(response.data, resolvedFileName);

      return {
        rows: [],
        fileName: resolvedFileName,
      };
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to download test-series report"),
      );
    }
  },
);

/**
 * Fetches the Members & Roles CSV report and triggers a file download.
 * @param args - Requester NUID and optional sort parameters.
 * @returns Download metadata for the completed request.
 */
export const downloadMembersRolesReport = createAsyncThunk(
  "teamReport/downloadMembersRolesReport",
  async (
    { nuid, pageId, sortColumn, sortOrder }: DownloadMembersRolesReportArgs,
    { rejectWithValue },
  ) => {
    try {
      const response = await api.get(endpoints.ubt.reports.membersRoles, {
        params: {
          ...(pageId ? { pageId } : {}),
          ...(sortColumn ? { sortColumn } : {}),
          ...(sortOrder ? { sortOrder } : {}),
        },
        headers: {
          accept: "text/csv",
          "x-nuid": nuid,
        },
        responseType: "blob",
      });

      const resolvedFileName = resolveCsvFileName(
        response.headers?.["content-disposition"],
        MEMBERS_ROLES_DEFAULT_FILENAME,
      );

      downloadCsvFile(response.data, resolvedFileName);

      return {
        rows: [],
        fileName: resolvedFileName,
      };
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to download members & roles report"),
      );
    }
  },
);

/**
 * Fetches the PIP History CSV report and triggers a file download.
 * @param args - Requester NUID, pageId context, and optional teamId/sort params.
 * @returns Download metadata for the completed request.
 */
export const downloadPipHistoryReport = createAsyncThunk(
  "teamReport/downloadPipHistoryReport",
  async (args: DownloadPipHistoryReportArgs, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.ubt.reports.pipHistory, {
        params: buildPipHistoryQueryParams(args),
        headers: {
          accept: "text/csv",
          "x-nuid": args.nuid,
        },
        responseType: "blob",
      });

      const resolvedFileName = resolveCsvFileName(
        response.headers?.["content-disposition"],
        PIP_HISTORY_DEFAULT_FILENAME,
      );

      downloadCsvFile(response.data, resolvedFileName);

      return {
        rows: [],
        fileName: resolvedFileName,
      };
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to download PIP history report"),
      );
    }
  },
);

const GL_DATA_BY_TEAM_DEFAULT_FILENAME = "UBT_GL_DATA.csv";
const TEST_SERIES_BY_CRITERIA_DEFAULT_FILENAME = "TEST_SERIES.csv";

/**
 * Exports the GL data CSV for all teams belonging to the authenticated user.
 * The back-end resolves the team list from the NUID sent in the request header.
 *
 * @param args - Contains the authenticated user's NUID.
 * @returns Download metadata on success.
 */
export const exportGLDataByTeamIds = createAsyncThunk(
  "teamReport/exportGLDataByTeamIds",
  async ({ nuid }: ExportGLDataByTeamIdsArgs, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.ubt.reports.exportGLDataByTeamIds, {
        headers: {
          accept: "text/csv",
          "x-nuid": nuid,
        },
        responseType: "blob",
      });

      const resolvedFileName = resolveCsvFileName(
        response.headers?.["content-disposition"],
        GL_DATA_BY_TEAM_DEFAULT_FILENAME,
      );

      downloadCsvFile(response.data, resolvedFileName);

      return { rows: [], fileName: resolvedFileName };
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to export GL data report"),
      );
    }
  },
);

/**
 * Downloads the test-series CSV report filtered by arbitrary search criteria.
 * Used by the SearchUBTProjects page to pass session-storage-derived filters
 * directly to the `/ubt/reports/test-series` endpoint.
 *
 * @param args - Authenticated NUID and pre-built query param map.
 * @returns Download metadata on success.
 */
export const downloadTestSeriesByCriteriaReport = createAsyncThunk(
  "teamReport/downloadTestSeriesByCriteriaReport",
  async ({ nuid, params }: DownloadTestSeriesByCriteriaArgs, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.ubt.reports.testSeries, {
        params,
        headers: {
          accept: "text/csv",
          "x-nuid": nuid,
        },
        responseType: "blob",
      });

      const resolvedFileName = resolveCsvFileName(
        response.headers?.["content-disposition"],
        TEST_SERIES_BY_CRITERIA_DEFAULT_FILENAME,
      );

      downloadCsvFile(response.data, resolvedFileName);

      return { rows: [], fileName: resolvedFileName };
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to download test-series report"),
      );
    }
  },
);

const teamReportSlice = createSlice({
  name: "teamReport",
  initialState,
  reducers: {
    /**
     * Clears report download state and previous report rows.
     */
    resetTestSeriesReportState: (state) => {
      state.rows = [];
      state.loading = false;
      state.error = null;
      state.lastDownloadedFileName = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(downloadTestSeriesReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadTestSeriesReport.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.rows = action.payload.rows;
        state.lastDownloadedFileName = action.payload.fileName;
      })
      .addCase(downloadTestSeriesReport.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ??
          action.error.message ??
          "Failed to download test-series report";
      })
      .addCase(downloadMembersRolesReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadMembersRolesReport.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.rows = action.payload.rows;
        state.lastDownloadedFileName = action.payload.fileName;
      })
      .addCase(downloadMembersRolesReport.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ??
          action.error.message ??
          "Failed to download members & roles report";
      })
      .addCase(downloadPipHistoryReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadPipHistoryReport.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.rows = action.payload.rows;
        state.lastDownloadedFileName = action.payload.fileName;
      })
      .addCase(downloadPipHistoryReport.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ??
          action.error.message ??
          "Failed to download PIP history report";
      })
      .addCase(exportGLDataByTeamIds.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportGLDataByTeamIds.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.rows = action.payload.rows;
        state.lastDownloadedFileName = action.payload.fileName;
      })
      .addCase(exportGLDataByTeamIds.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ??
          action.error.message ??
          "Failed to export GL data report";
      })
      .addCase(downloadTestSeriesByCriteriaReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadTestSeriesByCriteriaReport.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.rows = action.payload.rows;
        state.lastDownloadedFileName = action.payload.fileName;
      })
      .addCase(downloadTestSeriesByCriteriaReport.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ??
          action.error.message ??
          "Failed to download test-series report";
      });
  },
});

export const { resetTestSeriesReportState } = teamReportSlice.actions;

/**
 * Selects test-series report state from the root store shape.
 * @param state - Root Redux state.
 * @returns Test-series report slice state.
 */
export const selectTestSeriesReportState = (state: {
  teamReportSlice: TestSeriesReportState;
}): TestSeriesReportState => state.teamReportSlice;

/**
 * Selects the loading state for report downloads.
 * @param state - Root Redux state.
 * @returns True if a report is currently being downloaded.
 */
export const selectReportDownloadLoading = (state: {
  teamReportSlice: TestSeriesReportState;
}): boolean => state.teamReportSlice?.loading ?? false;

export default teamReportSlice.reducer;
